import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";

// ── Şifre Analiz Motoru ───────────────────────────────────────────────────────

const COMMON_PASSWORDS = new Set([
  "123456","12345678","password","qwerty","abc123","111111","iloveyou","admin","letmein",
  "welcome","monkey","dragon","master","sunshine","princess","shadow","superman","football",
  "1234567890","123123","000000","qwerty123","password1","sifre","sifre123","parola",
  "parola123","turkey","turkiye","12345","1234","123","pass","login","test123"
]);

const KEYBOARD_PATTERNS = [
  "qwerty","qwertz","asdfgh","asdf","zxcvbn","zxcv",
  "123456","12345","1234","234567","345678","456789","567890",
  "abcdef","abcd","abcde","aaaa","bbbb","cccc","1111","2222","3333","4444"
];

// Yaygın isimler listesi
const COMMON_NAMES = [
  "ahmet","mehmet","mustafa","ali","hasan","huseyin","ibrahim","ismail","osman","yusuf",
  "ayse","fatma","emine","hatice","zeynep","elif","meryem","sultan","ozlem","gulsum",
  "serkan","burak","emre","can","ege","arda","berk","kaan","alp","deniz","onur","mert",
  "furkan","kerem","umut","erhan","oguz","tolga","selim","yilmaz","demir","celik",
  "john","jane","mike","david","chris","james","robert","william","mary","linda",
  "admin","user","test","guest","root","demo","info","mail","pass","login",
  "michael","daniel","thomas","mark","paul","andrew","richard","peter","george",
  "anna","emma","alice","jessica","sarah","emily","olivia","sophia","hannah","laura"
];

// E-postadan tüm anlamlı parçaları çıkar
// ornek@gmail.com → ["ornek"], serkanbolek@hotmail.com → ["serkanbolek","serkan","bolek"]
// ali.veli@domain.com → ["ali","veli","aliveli"]
function extractAllFromEmail(email) {
  if (!email || !email.includes("@")) return [];
  const lower = email.toLowerCase().trim();
  const atIdx = lower.indexOf("@");
  const userPart = lower.slice(0, atIdx); // "serkanbolek" veya "ali.veli"
  const parts = new Set();

  // Kullanıcı adının tamamı
  parts.add(userPart);

  // Nokta, tire, alt çizgi, rakamla bölünmüş parçalar
  userPart.split(/[.\-_]/).forEach(p => { if (p.length >= 2) parts.add(p); });

  // Rakamları çıkararak kalan harf grupları
  const lettersOnly = userPart.replace(/\d+/g, " ").trim();
  lettersOnly.split(/\s+/).forEach(p => { if (p.length >= 2) parts.add(p); });

  // Ardışık harf kombinasyonları (3+ harf grupları)
  const matches = userPart.match(/[a-z]{2,}/g) || [];
  matches.forEach(m => parts.add(m));

  return Array.from(parts).filter(p => p.length >= 2);
}

// Ardışık rakam kontrolü (123, 456, 321 vb.)
function hasSequentialNumbers(pw) {
  for (let i = 0; i <= pw.length - 3; i++) {
    const a = pw.charCodeAt(i);
    const b = pw.charCodeAt(i+1);
    const c = pw.charCodeAt(i+2);
    if ((b===a+1 && c===b+1) || (b===a-1 && c===b-1)) return true;
  }
  return false;
}

// İsim + rakam kombinasyonu: Serkan.123, ali2024, Mehmet! gibi
function detectNameNumberCombo(lowerPw, emailParts) {
  // Tüm kaynaklardan isim adayları: sabit liste + e-postadan çıkarılanlar
  const allCandidates = [...new Set([...COMMON_NAMES, ...emailParts])];

  return allCandidates.some(name => {
    if (name.length < 2 || !lowerPw.includes(name)) return false;
    const idx = lowerPw.indexOf(name);
    const before = lowerPw.slice(0, idx);
    const after  = lowerPw.slice(idx + name.length);
    // İsmin etrafında rakam var mı?
    return /\d/.test(before) || /\d/.test(after);
  });
}

function analyzePassword(pw, emailStr = "") {
  if (!pw) return null;
  const lower = pw.toLowerCase();

  // E-postadan isim parçalarını çıkar
  const emailParts = extractAllFromEmail(emailStr);

  const hasLower   = /[a-z]/.test(pw);
  const hasUpper   = /[A-Z]/.test(pw);
  const hasNumber  = /[0-9]/.test(pw);
  const hasSymbol  = /[!@#$%^&*()\-_=+[\]{};':",.<>/?\\|`~]/.test(pw);
  const isCommon   = COMMON_PASSWORDS.has(lower);
  const hasYear    = /19\d\d|20[012]\d/.test(pw);
  const hasKeyboardPattern = KEYBOARD_PATTERNS.some(p => lower.includes(p));
  const hasRepeat  = /(.)\1{2,}/.test(pw);
  const onlyLetters = /^[a-zA-Z]+$/.test(pw);
  const onlyNumbers = /^[0-9]+$/.test(pw);
  const seqNumbers  = hasSequentialNumbers(pw);

  // Kişisel bilgi: e-postadan çıkarılan parçalar şifrede var mı?
  const hasPersonalInfo = emailParts.some(p => p.length >= 3 && lower.includes(p));

  // İsim tespiti: sabit liste + e-postadan çıkarılanların HEPSİ
  const hasCommonName = [...COMMON_NAMES, ...emailParts].some(name =>
    name.length >= 2 && lower.includes(name)
  );

  // İsim+rakam kombinasyonu
  const nameNumCombo = detectNameNumberCombo(lower, emailParts);

  // ── Puanlama
  let lengthScore = 0;
  if      (pw.length >= 20) lengthScore = 35;
  else if (pw.length >= 16) lengthScore = 28;
  else if (pw.length >= 12) lengthScore = 18;
  else if (pw.length >= 8)  lengthScore = 8;

  let diversityScore = 0;
  if (hasLower)  diversityScore += 8;
  if (hasUpper)  diversityScore += 10;
  if (hasNumber) diversityScore += 10;
  if (hasSymbol) diversityScore += 12;
  if (!onlyLetters && !onlyNumbers) diversityScore += 5;

  const uniqueRatio = new Set(lower.split("")).size / pw.length;
  const uniqueScore = uniqueRatio > 0.85 ? 22 : uniqueRatio > 0.7 ? 15 : uniqueRatio > 0.5 ? 8 : 3;

  let penalty = 0;
  if (isCommon)           penalty += 65;
  if (hasKeyboardPattern) penalty += 22;
  if (hasRepeat)          penalty += 15;
  if (hasYear)            penalty += 14;
  if (seqNumbers)         penalty += 20;
  if (nameNumCombo)       penalty += 35;  // isim+rakam: en ağır ceza
  if (hasPersonalInfo && !nameNumCombo) penalty += 30; // e-posta bilgisi
  if (hasCommonName && !nameNumCombo && !hasPersonalInfo) penalty += 18;
  if (pw.length < 8)      penalty += 30;
  if (onlyNumbers)        penalty += 20;
  if (onlyLetters)        penalty += 10;

  let score = Math.max(0, Math.min(100, lengthScore + diversityScore + uniqueScore - penalty));

  const level = score >= 80 ? "Çok Güçlü" : score >= 60 ? "Güçlü" : score >= 35 ? "Orta" : "Zayıf";

  // Entropy & kırılma süresi
  let charset = 0;
  if (hasLower)  charset += 26;
  if (hasUpper)  charset += 26;
  if (hasNumber) charset += 10;
  if (hasSymbol) charset += 32;
  if (charset === 0) charset = 26;
  const entropy = Math.round(Math.log2(Math.pow(charset, pw.length)));

  // ── Gerçekçi kırılma süresi hesabı ──────────────────────────────────────
  // Saf brute force değil, gerçek saldırı senaryoları baz alınır:
  // - Yaygın liste saldırısı: anında
  // - Sözlük + kural (hashcat rule): ~1 milyar deneme/sn (GPU)
  // - Hibrit (isim+rakam pattern): ~500 milyon deneme/sn
  // - Saf brute force (yüksek entropy): ~10 milyar deneme/sn (modern GPU cluster)
  let crackSec;
  if (isCommon) {
    crackSec = 0;
  } else if (nameNumCombo || hasPersonalInfo) {
    // İsim+rakam: hashcat "rule-based" ile saniyeler içinde — çok düşük etkin alan
    const effectiveSpace = Math.min(Math.pow(charset, pw.length), 5e8);
    crackSec = effectiveSpace / 1e9; // 1 milyar/sn GPU
  } else if (hasCommonName || hasKeyboardPattern || seqNumbers || hasYear) {
    // Sözlük bazlı + kural uygulaması
    const effectiveSpace = Math.min(Math.pow(charset, pw.length), Math.pow(charset, Math.min(pw.length, 8)));
    crackSec = effectiveSpace / 1e9;
  } else if (pw.length <= 8) {
    // Kısa şifre: modern GPU ile tam brute force mümkün
    crackSec = Math.pow(charset, pw.length) / 1e10; // 10 milyar/sn
  } else {
    // Gerçekten rastgele, uzun şifre: saf brute force
    crackSec = Math.pow(charset, pw.length) / 1e10;
  }

  const formatCrack = (s) => {
    if (s <= 0)               return "Anında (listede var!)";
    if (s < 1)                return "< 1 saniye";
    if (s < 60)               return `~${Math.round(s)} saniye`;
    if (s < 3600)             return `~${Math.round(s/60)} dakika`;
    if (s < 86400)            return `~${Math.round(s/3600)} saat`;
    if (s < 2592000)          return `~${Math.round(s/86400)} gün`;
    if (s < 31536000)         return `~${Math.round(s/2592000)} ay`;
    if (s < 31536000*1e2)     return `~${Math.round(s/31536000)} yıl`;
    if (s < 31536000*1e5)     return `~${(s/31536000/1e3).toFixed(0)}K yıl`;
    if (s < 31536000*1e8)     return `~${(s/31536000/1e6).toFixed(0)}M yıl`;
    if (s < 31536000*1e11)    return `~${(s/31536000/1e9).toFixed(0)}B yıl`;
    return "> Evrenin ömrü";
  };
  const crackLabel = formatCrack(crackSec);

  const warnings = [];
  if (isCommon)           warnings.push("⚠️ Bu şifre yaygın zayıf listesinde yer alıyor");
  if (nameNumCombo)       warnings.push("⚠️ İsim + rakam kombinasyonu — tahmin edilmesi çok kolay");
  if (hasPersonalInfo && !nameNumCombo) warnings.push("⚠️ E-posta adresindeki kişisel bilgiyi içeriyor");
  if (hasKeyboardPattern) warnings.push("⚠️ Klavye sırası içeriyor (qwerty, asdf…)");
  if (seqNumbers)         warnings.push("⚠️ Ardışık rakam dizisi içeriyor (123, 456…)");
  if (hasRepeat)          warnings.push("⚠️ Tekrar eden karakterler var (aaa, 111…)");
  if (hasYear)            warnings.push("⚠️ Yıl/tarih bilgisi içeriyor");
  if (hasCommonName && !nameNumCombo && !hasPersonalInfo) warnings.push("⚠️ Yaygın isim içeriyor");
  if (pw.length < 8)      warnings.push("⚠️ Çok kısa — en az 8 karakter önerilir");
  if (onlyNumbers)        warnings.push("⚠️ Yalnızca rakamlardan oluşuyor");

  return {
    score, level, entropy, crackLabel, charset,
    hasLower, hasUpper, hasNumber, hasSymbol,
    isCommon, hasYear, hasKeyboardPattern, hasRepeat,
    hasPersonalInfo, hasCommonName, nameNumCombo, seqNumbers,
    onlyNumbers, onlyLetters, warnings,
    emailPartsFound: emailParts.filter(p => p.length >= 2 && lower.includes(p)),
  };
}

const LEVEL_COLOR = { "Çok Güçlü":"#22c55e", "Güçlü":"#86efac", "Orta":"#f59e0b", "Zayıf":"#ef4444" };
const LEVEL_EMOJI = { "Çok Güçlü":"😎", "Güçlü":"🙂", "Orta":"🤔", "Zayıf":"😟" };

export default function SifreOlcer() {
  const [sifre, setSifre]   = useState("");
  const [email, setEmail]   = useState("");
  const [analiz, setAnaliz] = useState(null);
  const [emailHata, setEmailHata] = useState(false);

  useEffect(() => {
    if (sifre && email && !email.includes("@")) {
      setEmailHata(true);
    } else {
      setEmailHata(false);
    }
    setAnaliz(analyzePassword(sifre, email));
  }, [sifre, email]);

  const renk  = analiz ? LEVEL_COLOR[analiz.level] : "#64748b";
  const emoji = analiz ? LEVEL_EMOJI[analiz.level] : "🔒";

  const kriterler = analiz ? [
    { ok: analiz.hasLower,            label: "Küçük harf (a–z)" },
    { ok: analiz.hasUpper,            label: "Büyük harf (A–Z)" },
    { ok: analiz.hasNumber,           label: "Rakam (0–9)" },
    { ok: analiz.hasSymbol,           label: "Özel karakter (!@#…)" },
    { ok: sifre.length >= 12,         label: "12+ karakter" },
    { ok: !analiz.isCommon,           label: "Yaygın şifre değil" },
    { ok: !analiz.hasKeyboardPattern, label: "Klavye sırası yok" },
    { ok: !analiz.seqNumbers,         label: "Ardışık rakam yok" },
    { ok: !analiz.nameNumCombo,       label: "İsim+rakam combo yok" },
    { ok: !analiz.hasPersonalInfo,    label: "Kişisel bilgi yok" },
  ] : [];

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)", fontFamily:"'Sora',sans-serif" }}>
      <Navbar />
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .so-input:focus{border-color:#a78bfa !important;box-shadow:0 0 0 3px rgba(167,139,250,0.2);}
        .so-input::placeholder{color:#475569;}
      `}</style>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",padding:"2rem",minHeight:"calc(100vh - 54px)" }}>
        <div style={{ background:"rgba(255,255,255,0.04)",backdropFilter:"blur(20px)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:"24px",padding:"2.5rem",width:"100%",maxWidth:"540px",boxShadow:"0 25px 50px rgba(0,0,0,0.5)" }}>

          <div style={{ textAlign:"center",marginBottom:"2rem" }}>
            <span style={{ fontSize:"3rem",display:"block",marginBottom:"0.5rem" }}>🛡️</span>
            <h1 style={{ margin:0,fontSize:"1.8rem",fontWeight:"800",background:"linear-gradient(90deg,#a78bfa,#60a5fa)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>
              Canlı Şifre Gücü Ölçer
            </h1>
            <p style={{ color:"#94a3b8",fontSize:"0.9rem",marginTop:"0.4rem" }}>Şifreni gerçekçi bir analizle değerlendir!</p>
          </div>

          {/* E-posta — isim tespiti için kullanılır */}
          <div style={{ marginBottom:"0.75rem" }}>
            <input className="so-input"
              style={{ width:"100%",boxSizing:"border-box",background:"rgba(255,255,255,0.05)",border:`2px solid ${emailHata?"#ef4444":"rgba(255,255,255,0.1)"}`,borderRadius:"14px",padding:"0.8rem 1.1rem",color:"#fff",fontSize:"0.9rem",outline:"none",transition:"border-color .2s",fontFamily:"'Sora',sans-serif" }}
              type="email"
              placeholder="E-posta adresin (isim tespiti için gir)"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="off"
            />
            {email && !emailHata && (
              <div style={{ color:"#86efac",fontSize:"0.73rem",marginTop:"0.3rem",paddingLeft:"0.3rem" }}>
                ✓ E-postadan isim analizi aktif
                {analiz?.emailPartsFound?.length > 0 &&
                  <span style={{ color:"#fca5a5",marginLeft:"0.4rem" }}>
                    — "{analiz.emailPartsFound.slice(0,3).join('", "')}" şifrede tespit edildi
                  </span>
                }
              </div>
            )}
          </div>

          {/* Şifre */}
          <div style={{ display:"flex",alignItems:"center",gap:"0.75rem",marginBottom:"1rem" }}>
            <input className="so-input"
              style={{ flex:1,background:"rgba(255,255,255,0.08)",border:"2px solid rgba(255,255,255,0.15)",borderRadius:"16px",padding:"1rem 1.25rem",color:"#fff",fontSize:"1.2rem",fontFamily:"monospace",outline:"none",transition:"border-color .2s" }}
              type="text"
              placeholder="Şifreni buraya yaz..."
              value={sifre}
              onChange={e => setSifre(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            <span style={{ fontSize:"2rem",minWidth:"48px",textAlign:"center" }}>{emoji}</span>
          </div>

          {sifre && analiz && (
            <div style={{ animation:"fadeIn 0.3s ease" }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:"0.4rem" }}>
                <span style={{ color:renk,fontWeight:"700",fontSize:"1.05rem" }}>{analiz.level}</span>
                <span style={{ color:"#94a3b8",fontSize:"0.85rem" }}>{analiz.score}/100 puan</span>
              </div>
              <div style={{ background:"rgba(255,255,255,0.08)",borderRadius:"999px",height:"14px",overflow:"hidden",marginBottom:"1.25rem" }}>
                <div style={{ height:"100%",borderRadius:"999px",background:renk,width:`${analiz.score}%`,transition:"width 0.4s ease",boxShadow:`0 0 12px ${renk}80` }} />
              </div>

              {analiz.warnings.length > 0 && (
                <div style={{ background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:"12px",padding:"0.85rem 1rem",marginBottom:"1.25rem" }}>
                  {analiz.warnings.map((w,i) => (
                    <div key={i} style={{ color:"#fca5a5",fontSize:"0.82rem",marginBottom:i<analiz.warnings.length-1?"0.3rem":0,fontWeight:"500" }}>{w}</div>
                  ))}
                </div>
              )}

              <p style={{ color:"#64748b",fontSize:"0.75rem",fontWeight:"700",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"0.6rem",marginTop:0 }}>Kontrol Listesi</p>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.35rem",marginBottom:"1.25rem" }}>
                {kriterler.map(({ ok, label }) => (
                  <div key={label} style={{ display:"flex",alignItems:"center",gap:"0.45rem",padding:"0.45rem 0.6rem",borderRadius:"8px",background:ok?"rgba(34,197,94,0.08)":"rgba(255,255,255,0.03)",border:ok?"1px solid rgba(34,197,94,0.2)":"1px solid transparent",transition:"all .3s" }}>
                    <span style={{ fontSize:"0.9rem",flexShrink:0 }}>{ok ? "✅" : "❌"}</span>
                    <span style={{ color:ok?"#86efac":"#64748b",fontSize:"0.78rem",fontWeight:ok?"600":"400",lineHeight:"1.3" }}>{label}</span>
                  </div>
                ))}
              </div>

              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0.6rem",marginBottom:"1rem" }}>
                {[
                  { val:analiz.entropy,    label:"Entropy (bit)", color:"#60a5fa", size:"1.2rem" },
                  { val:analiz.crackLabel, label:"Kırılma Süresi", color:"#fbbf24", size:"0.78rem" },
                  { val:analiz.charset,    label:"Karakter Seti",  color:"#a78bfa", size:"1.2rem" },
                ].map(s => (
                  <div key={s.label} style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"10px",padding:"0.75rem",textAlign:"center" }}>
                    <div style={{ color:s.color,fontSize:s.size,fontWeight:"800",lineHeight:"1.3" }}>{s.val}</div>
                    <div style={{ color:"#475569",fontSize:"0.7rem",marginTop:"0.2rem" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {analiz.score >= 80 && (
                <div style={{ background:"linear-gradient(135deg,rgba(34,197,94,0.15),rgba(16,185,129,0.15))",border:"2px solid rgba(34,197,94,0.3)",borderRadius:"14px",padding:"1.1rem",textAlign:"center" }}>
                  <span style={{ fontSize:"2rem",display:"block",marginBottom:"0.4rem" }}>🎉</span>
                  <p style={{ color:"#86efac",fontSize:"0.95rem",fontWeight:"600",margin:0,lineHeight:"1.5" }}>
                    Harika! Güçlü bir şifre oluşturdun.<br/>Siber saldırılara karşı güvendesin!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
